import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { DatePickerNew, Input, Select, TextArea } from 'common-src/components/base';
import { apiRequest, getRestErrorMessage, HttpMethods } from 'common-src/features/rest';
import { downloadPDF } from 'common-src/utils/pdfUtils';

import { AddButton } from 'src/components/buttons';
import usePopup from 'src/hooks/usePopup';
import BasePopup from 'src/popups/BasePopup';

import { initialState, readingType } from './constants';
import { generatePDFName, getIsButtonEnabled, getPayload, validateData } from './helpers';
import styles from './RequestLmnPopup.module.scss';

const RequestMnlPopup = ({ patientId, onClose }) => {
  const [data, setData] = useState(initialState);
  const [showEmail, setShowEmail] = useState(false);

  const { errors, setErrors, renderButtons } = usePopup();

  const [generatedContent, setGeneratedContent] = useState('');
  const [showResponseEditor, setShowResponseEditor] = useState(false);

  const onChangeHandler = (key, value) => {
    const dataCopy = JSON.parse(JSON.stringify(data));
    const newData = _.set(dataCopy, key, value);
    setData(newData);

    setErrors((prev) => ({ ...prev, ...validateData(key, value, newData) }));
  };

  const onDateChangeHandler = (key, keyToCompare, isBefore, value) => {
    const newData = { ...data, [key]: value };
    const err = { ...validateData(key, value, newData) };

    const compareCondition = isBefore
      ? moment(value).isAfter(moment(newData?.[keyToCompare]))
      : moment(value).isBefore(moment(newData?.[keyToCompare]));

    if (!compareCondition) {
      if (newData[key]) {
        Object.assign(err, { ...validateData(key, value, newData) });
      }
      if (newData[keyToCompare]) {
        Object.assign(err, { ...validateData(keyToCompare, newData?.[keyToCompare], newData) });
      }
    }

    setErrors((prev) => ({ ...prev, ...err }));
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const onSubmitHandler = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();
    apiRequest({
      endpoint: `members/${patientId}/generateLetterOfMedicalNecessity`,
      queryParams: {},
      body: getPayload(data),
      method: HttpMethods.Post,
    }).then(async (res) => {
      const response = await res.json();

      if (!res.ok) {
        const err = await res.json();
        const errMsg = getRestErrorMessage(err) || '';
        errorCallback(errMsg);
        return;
      }

      setGeneratedContent(response.msg);
      setShowResponseEditor(true);
      successCallback('Letter generated!');
    });
  };

  const onApproveHandler = (loadingCallback, successCallback, errorCallback) => {
    loadingCallback();

    apiRequest({
      endpoint: `members/${patientId}/approveLetter`,
      queryParams: {},
      body: { letterContent: generatedContent, ...(showEmail ? { emailTo: data.email } : {}) },
      method: HttpMethods.Post,
    }).then(async (res) => {
      if (!res.ok) {
        const err = await res.json();
        const errMsg = getRestErrorMessage(err) || '';
        errorCallback(errMsg);
        return;
      }

      if (!showEmail) {
        downloadPDF(res, generatePDFName(data, patientId));
      }

      successCallback(showEmail ? 'Email sent!' : 'Letter downloaded!');
      onClose(true);
    });
  };

  const renderEmail = () => {
    if (showEmail) {
      return (
        <div className={['flex-row', 'vertically-centered', 'gap-1'].join(' ')}>
          <Input
            id="email"
            classNames={['flex-1', 'm-b-0']}
            label="Email address"
            placeholder="Enter an email address"
            onTextChange={(text) => onChangeHandler('email', text)}
            value={data.email}
            errorText={errors.email}
            required
          />
          <img
            className="icon-btn"
            src={CommonIcons.closeIcon}
            style={{ marginLeft: '8px', alignSelf: 'center' }}
            alt="reset-icon"
            role="presentation"
            onClick={() => {
              setShowEmail(false);
              onChangeHandler('email', '');
            }}
          />
        </div>
      );
    }

    return (
      <AddButton
        id="show-email-input"
        label="Send email instead of download"
        onClick={() => setShowEmail(true)}
        classNames={['m-b-1']}
      />
    );
  };

  return (
    <BasePopup id="generate-mnl" open onClose={onClose} title="Generated Content">
      {showResponseEditor ? (
        <div className={styles.wrapper}>
          <TextArea
            id="generatedContent"
            className="no-border"
            value={generatedContent}
            onTextChange={setGeneratedContent}
            fixedRows={20}
            noBorder
          />
          {renderEmail()}
        </div>
      ) : (
        <div className={['p-l-30', 'p-r-30', 'p-t-20', 'p-b-5'].join(' ')}>
          <div className={styles.grid}>
            <Select
              id="readingType"
              classNames={[styles.width50]}
              label="Glucose Reading Type"
              options={readingType}
              onChange={(op) => onChangeHandler('readingType', op)}
              value={data.readingType}
            />
          </div>
          <p className={[styles.error, 'font-s-12', 'm-t-5', 'm-b-30', 'm-l-8'].join(' ')}>
            {errors.types}
          </p>
          <div className={['flex-row', 'gap-40', 'm-b-20'].join(' ')}>
            <DatePickerNew
              id="start-date"
              header="Start date"
              placeholder="Select date"
              maxDate={new Date()}
              value={data.startDate}
              onDateSelected={(date) =>
                onDateChangeHandler(
                  'startDate',
                  'endDate',
                  true,
                  date ? moment(date).format('YYYY-MM-DD') : null,
                )
              }
              onBlur={(date) =>
                setErrors((prev) => ({ ...prev, ...validateData('startDate', date, data) }))
              }
              errorText={errors.startDate}
              required
            />
            <DatePickerNew
              id="end-date"
              header="End date"
              placeholder="Select date"
              maxDate={new Date()}
              value={data.endDate}
              onDateSelected={(date) =>
                onDateChangeHandler(
                  'endDate',
                  'startDate',
                  false,
                  date ? moment(date).format('YYYY-MM-DD') : null,
                )
              }
              onBlur={(date) =>
                setErrors((prev) => ({ ...prev, ...validateData('endDate', date, data) }))
              }
              errorText={errors.endDate}
              required
            />
          </div>
          <p className={[styles.error, 'font-s-12', 'm-t-5', 'm-b-30', 'm-l-8'].join(' ')}>
            {errors.types}
          </p>
        </div>
      )}
      {renderButtons({
        containerClassName: styles.buttonsContainer,
        submitButtonText: showResponseEditor
          ? showEmail
            ? 'Send and Close'
            : 'Download and Close'
          : 'Submit',
        onClose,
        onSubmit: showResponseEditor ? onApproveHandler : onSubmitHandler,
        isSubmitEnabled: showResponseEditor
          ? !!generatedContent.trim() && (!showEmail || !!data.email?.trim())
          : getIsButtonEnabled(data, errors),
      })}
    </BasePopup>
  );
};

RequestMnlPopup.propTypes = {
  onClose: PropTypes.func,
  patientId: PropTypes.number,
};

export default RequestMnlPopup;
